export const ATTACHMENTS_BUCKET =
  import.meta.env.VITE_ATTACHMENTS_BUCKET || "attachments";

/** Lifetime of the signed URLs handed to the UI (1 hour). */
export const SIGNED_URL_TTL_SECONDS = 60 * 60;

const STORAGE_URL_PATTERN =
  /\/storage\/v1\/object\/(?:public|authenticated|sign)\/([^/]+)\/(.+)$/;
const URL_SCHEME_PATTERN = /^[a-z][a-z0-9+.-]*:/i;

const safelyDecode = (value: string) => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const pathFromStoragePathname = (pathname: string, bucket: string) => {
  const match = STORAGE_URL_PATTERN.exec(pathname);
  if (!match || safelyDecode(match[1]) !== bucket) return null;
  return safelyDecode(match[2]) || null;
};

/**
 * Maps a stored attachment value (object path, `<bucket>/<path>`, legacy public
 * URL, authenticated/signed object URL) to its bucket object path.
 * Returns null for anything that is not a bucket object: external http URLs,
 * `data:` / `blob:` URLs, site-relative paths.
 */
export const getAttachmentPath = (
  value: string | null | undefined,
  bucket: string = ATTACHMENTS_BUCKET,
): string | null => {
  const trimmed = value?.trim();
  if (!trimmed) return null;

  if (URL_SCHEME_PATTERN.test(trimmed) || trimmed.startsWith("//")) {
    try {
      return pathFromStoragePathname(
        new URL(trimmed, "http://localhost").pathname,
        bucket,
      );
    } catch {
      return null;
    }
  }

  const withoutQuery = trimmed.split(/[?#]/)[0];
  if (withoutQuery.startsWith("/")) {
    return pathFromStoragePathname(withoutQuery, bucket);
  }

  const path = safelyDecode(withoutQuery);
  const withoutBucket = path.startsWith(`${bucket}/`)
    ? path.slice(bucket.length + 1)
    : path;
  return withoutBucket || null;
};

/** Resolves an RAFile-like value: its `path` field wins, else its `src`. */
export const getAttachmentPathFromFile = (file: {
  path?: string | null;
  src?: string | null;
}): string | null =>
  getAttachmentPath(file.path) ?? getAttachmentPath(file.src);

type CacheEntry = { url: string; expiresAt: number };

/** Signed URLs are reused until this close to expiry. */
const EXPIRY_MARGIN_MS = 5 * 60 * 1000;

/**
 * Creates a batching signer with a per-instance cache. `signPaths` must return
 * a signed URL per path (null when signing failed). Values that do not resolve
 * to a bucket object, or whose signing failed, are returned unchanged.
 */
export const createSignedUrlResolver = (
  signPaths: (paths: string[]) => Promise<Array<string | null>>,
  now: () => number = Date.now,
) => {
  const cache = new Map<string, CacheEntry>();
  const pending = new Map<string, Promise<string | null>>();

  return async (values: string[]): Promise<string[]> => {
    const paths = values.map((value) => getAttachmentPath(value));
    const missing = Array.from(
      new Set(
        paths.filter(
          (path): path is string =>
            path != null &&
            !pending.has(path) &&
            (cache.get(path)?.expiresAt ?? 0) - now() <= EXPIRY_MARGIN_MS,
        ),
      ),
    );

    if (missing.length > 0) {
      const request = signPaths(missing).catch(() => missing.map(() => null));
      missing.forEach((path, index) => {
        pending.set(
          path,
          request.then((urls) => {
            const url = urls[index] ?? null;
            if (url) {
              cache.set(path, {
                url,
                expiresAt: now() + SIGNED_URL_TTL_SECONDS * 1000,
              });
            }
            pending.delete(path);
            return url;
          }),
        );
      });
    }

    return Promise.all(
      values.map(async (value, index) => {
        const path = paths[index];
        if (path == null) return value;
        const url = (await pending.get(path)) ?? cache.get(path)?.url;
        return url ?? value;
      }),
    );
  };
};
